import { base64ToImageData } from '../components/annotations/canvas/utils';

// Default epsilon for Douglas-Peucker simplification.
// Higher = fewer points, less detail. 3px balances fidelity vs. gRPC payload size.
export const DEFAULT_POLYGON_EPSILON = 3;

/**
 * Decodes a COCO column-major RLE mask to an ImageData bitmap.
 * counts alternates between runs of 0s and 1s, traversed column by column.
 */
function decodeRle(rle: RleMask): ImageData {
  const {
    counts,
    size
  } = rle;
  const [height, width] = size;
  const data = new Uint8ClampedArray(width * height * 4);
  let pixel = 0;
  let value = 0;
  for (const run of counts) {
    for (let i = 0; i < run; i++) {
      if (value === 1) {
        // column-major: pixel index → (col, row) → row-major index
        const col = Math.floor(pixel / height);
        const row = pixel % height;
        const index = (row * width + col) * 4;
        data[index] = 255;
        data[index + 1] = 255;
        data[index + 2] = 255;
        data[index + 3] = 255;
      }
      pixel++;
    }
    value = value === 0 ? 1 : 0;
  }
  return new ImageData(data, width, height);
}

// Moore neighborhood directions: clockwise starting from left.
const MOORE_DIRS = ([[-1, 0], [-1, -1], [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1]] as [number, number][]);

// SAM masks are white-on-black PNGs. Check R > 128 rather than alpha > 0.
function isOpaque(data: Uint8ClampedArray, width: number, height: number, x: number, y: number): boolean {
  if (x < 0 || x >= width || y < 0 || y >= height) return false;
  return (data[(y * width + x) * 4] ?? 0) > 128;
}

// Returns the topmost-leftmost opaque pixel, or null if the image is empty.
function findContourStart(data: Uint8ClampedArray, width: number, height: number): [number, number] | null {
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (isOpaque(data, width, height, x, y)) return [x, y];
    }
  }
  return null;
}

// One step of the Moore tracer: backtrack to entry direction, sweep clockwise for the next boundary pixel.
// Returns { x, y, dir } of the next pixel, or null if no neighbor found (isolated pixel).
function nextContourPixel(data: Uint8ClampedArray, width: number, height: number, cx: number, cy: number, entryDir: number): {
  x: number;
  y: number;
  dir: number;
} | null {
  const backDir = (entryDir + 4) % 8;
  for (let i = 1; i <= 8; i++) {
    const searchDir = (backDir + i) % 8;
    const [dx, dy] = MOORE_DIRS[searchDir]!;
    const nx = cx + dx;
    const ny = cy + dy;
    if (isOpaque(data, width, height, nx, ny)) {
      return {
        x: nx,
        y: ny,
        dir: searchDir
      };
    }
  }
  return null;
}

/**
 * Moore neighborhood contour tracer (Jacob's stopping criterion).
 * Returns boundary pixels in sequential clockwise order — safe for Douglas-Peucker.
 *
 * Only traces the outermost contour starting from the topmost-leftmost opaque pixel.
 * This is sufficient for single-region SAM masks.
 */
function traceContourOrdered(imageData: ImageData): [number, number][] {
  const {
    width,
    height,
    data
  } = imageData;
  const start = findContourStart(data, width, height);
  if (!start) return [];
  const [startX, startY] = start;
  const contour: [number, number][] = [];
  let cx = startX;
  let cy = startY;
  // Start is topmost-leftmost foreground, so the pixel above (dir 2 = [0, -1]) is background.
  // We treat the tracer as arriving from above (opposite dir 6 = [0, 1]).
  // Backtracking from dir 6 starts a clockwise sweep from dir 2, following the top edge.
  let entryDir = 6;
  const maxIterations = width * height;

  // End tracing on the second visit to the start pixel.
  // The first visit is the initial point; the second means the boundary loop is complete.
  // This avoids fragile direction-based stop checks tied to initial entry direction.
  let started = false;
  for (let iterations = 0; iterations < maxIterations; iterations++) {
    if (cx === startX && cy === startY) {
      if (started) break;
      started = true;
    }
    contour.push([cx, cy]);
    const next = nextContourPixel(data, width, height, cx, cy, entryDir);
    if (!next) break;
    cx = next.x;
    cy = next.y;
    entryDir = next.dir;
  }
  return contour;
}
export type RleMask = {
  counts: number[];
  size: [number, number]; // [height, width]
};
export type PolygonConversionResult = {
  points: [number, number][];
  rawPointCount: number;
  simplifiedPointCount: number;
  epsilonUsed: number;
  rawContour: [number, number][];
};
export async function base64MaskToPolygonResult(base64Mask: string, epsilon: number = DEFAULT_POLYGON_EPSILON): Promise<PolygonConversionResult> {
  const imageUrl = base64Mask.startsWith('data:') ? base64Mask : `data:image/png;base64,${base64Mask}`;
  const imageData = await base64ToImageData(imageUrl);
  if (!imageData) return {
    points: [],
    rawPointCount: 0,
    simplifiedPointCount: 0,
    epsilonUsed: epsilon,
    rawContour: []
  };
  const contour = traceContourOrdered(imageData);
  if (contour.length === 0) return {
    points: [],
    rawPointCount: 0,
    simplifiedPointCount: 0,
    epsilonUsed: epsilon,
    rawContour: contour
  };

  // Retry with lower epsilon if DP over-simplifies a small/thin mask to ≤2 points.
  // Ladder: requested epsilon → 2 → 1 (only steps strictly below the initial value are tried).
  const candidates = [epsilon, ...[2, 1].filter(e => e < epsilon)];
  let points: [number, number][] = [];
  let epsilonUsed = epsilon;
  for (const candidate of candidates) {
    const simplified = douglasPeucker(contour, candidate);
    epsilonUsed = candidate;
    if (simplified.length > 2) {
      points = simplified;
      break;
    }
  }
  return {
    points,
    rawPointCount: contour.length,
    simplifiedPointCount: points.length,
    epsilonUsed,
    rawContour: contour
  };
}
export function rleMaskToPolygonPoints(rle: RleMask, epsilon: number = DEFAULT_POLYGON_EPSILON): [number, number][] {
  const imageData = decodeRle(rle);
  return imageDataToPolygonPoints(imageData, epsilon);
}
function imageDataToPolygonPoints(imageData: ImageData, epsilon: number): [number, number][] {
  const contour = traceContourOrdered(imageData);
  if (contour.length === 0) return [];
  const simplified = douglasPeucker(contour, epsilon);
  return simplified.length <= 2 ? [] : simplified;
}

/**
 * Douglas-Peucker polyline simplification.
 * Reduces a pixel-level boundary (thousands of points) to a compact polygon.
 * epsilon: maximum perpendicular distance tolerance in pixels.
 * For ref - https://cartography-playground.gitlab.io/playgrounds/douglas-peucker-algorithm/
 */
export function douglasPeucker(points: [number, number][], epsilon: number): [number, number][] {
  if (points.length <= 2) return points;
  let maxDistance = 0;
  let splitIndex = 0;
  const startPoint = points[0]!;
  const endPoint = points[points.length - 1]!;
  for (let pointIndex = 1; pointIndex < points.length - 1; pointIndex++) {
    const distance = pointToSegmentDistance(points[pointIndex]!, startPoint, endPoint);
    if (distance > maxDistance) {
      maxDistance = distance;
      splitIndex = pointIndex;
    }
  }
  if (maxDistance > epsilon) {
    const left = douglasPeucker(points.slice(0, splitIndex + 1), epsilon);
    const right = douglasPeucker(points.slice(splitIndex), epsilon);
    return [...left.slice(0, -1), ...right];
  }
  return [startPoint, endPoint];
}
function pointToSegmentDistance(point: [number, number], segmentStart: [number, number], segmentEnd: [number, number]): number {
  const [startX, startY] = segmentStart;
  const [endX, endY] = segmentEnd;
  const [pointX, pointY] = point;
  const segmentDeltaX = endX - startX;
  const segmentDeltaY = endY - startY;
  const segmentLengthSquared = segmentDeltaX * segmentDeltaX + segmentDeltaY * segmentDeltaY;
  if (segmentLengthSquared === 0) {
    const deltaX = pointX - startX;
    const deltaY = pointY - startY;
    return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  }
  const projectionRatioUnclamped = ((pointX - startX) * segmentDeltaX + (pointY - startY) * segmentDeltaY) / segmentLengthSquared;
  const projectionRatio = Math.max(0, Math.min(1, projectionRatioUnclamped));
  const closestPointX = startX + projectionRatio * segmentDeltaX;
  const closestPointY = startY + projectionRatio * segmentDeltaY;
  const deltaX = closestPointX - pointX;
  const deltaY = closestPointY - pointY;
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
}